/**
 * Detects if the user is in the European Union based on timezone and IP geolocation.
 * Returns true if the user appears to be in an EU country.
 */
export async function isUserInEU(): Promise<boolean> {
  // EU country codes (including UK for now, though they left EU, GDPR still applies in some cases)
  const EU_COUNTRIES = [
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU',
    'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'
  ];

  // Method 1: Check timezone (quick check)
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const euTimezones = [
    'Europe/Amsterdam', 'Europe/Athens', 'Europe/Berlin', 'Europe/Brussels',
    'Europe/Budapest', 'Europe/Copenhagen', 'Europe/Dublin', 'Europe/Helsinki',
    'Europe/Lisbon', 'Europe/London', 'Europe/Luxembourg', 'Europe/Madrid',
    'Europe/Paris', 'Europe/Prague', 'Europe/Rome', 'Europe/Stockholm',
    'Europe/Vienna', 'Europe/Warsaw', 'Europe/Zagreb', 'Europe/Bratislava',
    'Europe/Bucharest', 'Europe/Sofia', 'Europe/Tallinn', 'Europe/Vilnius',
    'Europe/Riga', 'Europe/Nicosia', 'Europe/Valletta'
  ];

  if (euTimezones.some(tz => timezone.includes(tz.split('/')[1]))) {
    return true;
  }

  // Method 2: Try IP geolocation (fallback)
  try {
    // Using a free IP geolocation service
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (response.ok) {
      const data = await response.json();
      const countryCode = data.country_code;
      
      if (countryCode && EU_COUNTRIES.includes(countryCode.toUpperCase())) {
        return true;
      }
    }
  } catch (error) {
    console.warn('[GDPR Detection] IP geolocation failed, using timezone only:', error);
  }

  // Default: if we can't determine, be safe and show GDPR (conservative approach)
  // But actually, let's be more conservative: only show if we're confident
  return false;
}
